import logger from '../log';
import BaseInteraction from './BaseInteraction';
import { getVideoJsVideoElement } from '../common/utils/videoElementUtils';
import bindVideoJsToWebRTC from '../webrtc/bindVideoJsToWebRTC';
import { SourceType } from '../common/constants';
import ContentItem from '../api/content/ContentItem';
import {
  getMimeForSourceType,
  getSourceTypeForMime
} from '../common/utils/sourceTypeUtils';
import {
  ItemSource,
  PlayerNames,
  VideoJSOptions,
  VideoJSPlayer
} from '@kollective-frontend/ksdk-api';
import { BindOptions } from './interfaces';
import { Types, checkType, logUnableToSetupMessage } from '../util/checkTypes';
const log = logger.getLogger(__filename);

export const getMimeType = (item: ContentItem): string | undefined => {
  if (item && item.data && item.data.mime) {
    return item.data.mime;
  }
  const sourceType = getSourceTypeForMime(undefined, item.url);
  return (sourceType && getMimeForSourceType(sourceType)) || undefined;
};

export default class VideoJsInteraction extends BaseInteraction<VideoJSPlayer> {
  playerOptions: VideoJSOptions | undefined;

  constructor(player: VideoJSPlayer, playerOptions?: VideoJSOptions) {
    super(player);
    checkType(player, Types.VIDEOJS).emitErrorIfNotCorrectType();
    this.playerOptions = playerOptions;
  }

  bindInterceptor(
    shouldIntercept: () => boolean,
    getOptions: () => BindOptions
  ): void {
    super.bindInterceptor(shouldIntercept, getOptions);
    const result = checkType(this.player, Types.VIDEOJS);
    // XXX: The following should eventually be moved into the constructor,
    // after confirming that there are no complication based on change of timing.
    if (result.isType) {
      bindVideoJsToWebRTC(this.player, this.playerOptions || {}, this.binder);
    } else {
      logUnableToSetupMessage(PlayerNames.VIDEOJS_PLAYER, log, result);
    }
  }

  loader(items: ContentItem[]): ContentItem[] {
    const src = items.map(i => {
      return { src: i.url, type: getMimeType(i) };
    });
    this.setSource(src);
    return items;
  }

  setSource(src: ItemSource[]): void {
    const result = checkType(this.player, Types.VIDEOJS);
    if (result.isType) {
      this.player.src(src);
    } else {
      result.log(log, log.ERROR, 'Unable to set source');
    }
  }

  clearSource(): void {
    log.warn(this.generatePlannedErrorMessage('clearSource', 'VideoJs'));
    // Implementing this should be as simple as uncommenting this and testing it.
    // this.setSource(null);
  }

  videoElement(): HTMLVideoElement {
    return getVideoJsVideoElement(this.player);
  }

  getAllowedSourceTypes(): SourceType[] {
    return [SourceType.DASH, SourceType.HLS];
  }
}
